/**
 * Config store for dynamic configuration drawer state.
 *
 * Manages drawer visibility and pending configuration changes.
 * Does NOT persist - config is always loaded fresh from API.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  DynamicConfig,
  RateLimitsConfig,
} from '../types/api';
import { getDynamicConfig, updateDynamicConfig } from '../services/api';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Subset of config we allow editing.
 * V4 details rate limits only (discovery removed).
 */
export interface EditableConfig {
  rate_limits: Pick<
    RateLimitsConfig,
    'v4_details_rate' | 'v4_details_burst'
  >;
}

interface ConfigStoreState {
  // Drawer visibility
  isDrawerOpen: boolean;

  // Current config from API (null until loaded)
  serverConfig: DynamicConfig | null;

  // Form values (edited by user, may differ from serverConfig)
  formValues: EditableConfig | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;

  // Error message
  error: string | null;

  // Metadata
  source: 'redis' | 'environment' | null;
  updatedAt: string | null;
  updatedBy: string | null;

  // Actions
  openDrawer: () => Promise<void>;
  closeDrawer: () => void;
  updateFormValue: <K extends keyof EditableConfig>(
    section: K,
    field: keyof EditableConfig[K],
    value: EditableConfig[K][keyof EditableConfig[K]]
  ) => void;
  saveConfig: () => Promise<boolean>;
  resetForm: () => void;
}

// =============================================================================
// DEFAULT VALUES
// =============================================================================

const DEFAULT_EDITABLE_CONFIG: EditableConfig = {
  rate_limits: {
    v4_details_rate: 5.0,
    v4_details_burst: 10.0,
  },
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Extract editable config subset from full config.
 */
function extractEditableConfig(config: DynamicConfig): EditableConfig {
  return {
    rate_limits: {
      v4_details_rate: config.rate_limits.v4_details_rate,
      v4_details_burst: config.rate_limits.v4_details_burst,
    },
  };
}

/**
 * Check if form values differ from server config.
 */
export function hasChanges(
  formValues: EditableConfig | null,
  serverConfig: DynamicConfig | null
): boolean {
  if (!formValues || !serverConfig) return false;

  const serverEditable = extractEditableConfig(serverConfig);
  return JSON.stringify(formValues) !== JSON.stringify(serverEditable);
}

// =============================================================================
// STORE
// =============================================================================

export const useConfigStore = create<ConfigStoreState>()(
  immer((set, get) => ({
    isDrawerOpen: false,
    serverConfig: null,
    formValues: null,
    isLoading: false,
    isSaving: false,
    error: null,
    source: null,
    updatedAt: null,
    updatedBy: null,

    openDrawer: async () => {
      set((state) => {
        state.isDrawerOpen = true;
        state.isLoading = true;
        state.error = null;
      });

      try {
        const response = await getDynamicConfig();
        set((state) => {
          state.serverConfig = response.config;
          state.formValues = extractEditableConfig(response.config);
          state.source = response.source;
          state.updatedAt = response.updated_at;
          state.updatedBy = response.updated_by;
          state.isLoading = false;
        });
      } catch (err) {
        set((state) => {
          state.error = err instanceof Error ? err.message : 'Failed to load config';
          state.isLoading = false;
          // Use defaults if API fails
          state.formValues = { ...DEFAULT_EDITABLE_CONFIG };
        });
      }
    },

    closeDrawer: () => {
      set((state) => {
        state.isDrawerOpen = false;
        state.error = null;
      });
    },

    updateFormValue: (section, field, value) => {
      set((state) => {
        if (!state.formValues) return;

        if (section === 'rate_limits') {
          state.formValues = {
            ...state.formValues,
            rate_limits: {
              ...state.formValues.rate_limits,
              [field]: value,
            },
          };
        }
      });
    },

    saveConfig: async () => {
      const { formValues, serverConfig } = get();
      if (!formValues) return false;

      set((state) => {
        state.isSaving = true;
        state.error = null;
      });

      try {
        // Build partial update with only changed sections
        const updates: Record<string, unknown> = {};

        if (serverConfig) {
          // Check rate_limits changes
          const serverRates = {
            v4_details_rate: serverConfig.rate_limits.v4_details_rate,
            v4_details_burst: serverConfig.rate_limits.v4_details_burst,
          };
          if (JSON.stringify(serverRates) !== JSON.stringify(formValues.rate_limits)) {
            updates.rate_limits = formValues.rate_limits;
          }
        } else {
          // No server config, send everything
          updates.rate_limits = formValues.rate_limits;
        }

        if (Object.keys(updates).length === 0) {
          // No changes
          set((state) => {
            state.isSaving = false;
          });
          return true;
        }

        const response = await updateDynamicConfig(updates);

        if (response.success) {
          set((state) => {
            state.serverConfig = response.config;
            state.formValues = extractEditableConfig(response.config);
            state.updatedAt = response.updated_at;
            state.isSaving = false;
            state.isDrawerOpen = false;
          });

          return true;
        } else {
          set((state) => {
            state.error = 'Update failed';
            state.isSaving = false;
          });
          return false;
        }
      } catch (err) {
        set((state) => {
          state.error = err instanceof Error ? err.message : 'Failed to save config';
          state.isSaving = false;
        });
        return false;
      }
    },

    resetForm: () => {
      const { serverConfig } = get();
      set((state) => {
        if (serverConfig) {
          state.formValues = extractEditableConfig(serverConfig);
        } else {
          state.formValues = { ...DEFAULT_EDITABLE_CONFIG };
        }
        state.error = null;
      });
    },
  }))
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectIsDrawerOpen = (state: ConfigStoreState) => state.isDrawerOpen;
export const selectFormValues = (state: ConfigStoreState) => state.formValues;
export const selectIsLoading = (state: ConfigStoreState) => state.isLoading;
export const selectIsSaving = (state: ConfigStoreState) => state.isSaving;
export const selectError = (state: ConfigStoreState) => state.error;
export const selectSource = (state: ConfigStoreState) => state.source;
export const selectUpdatedAt = (state: ConfigStoreState) => state.updatedAt;
